import type { Team, TeamMember } from '../types/domain';

const SNAPSHOT_CACHE_VERSION = 'v2';
const STORAGE_KEY_PREFIX = 'cp.teamSettingsSnapshot';

export type TeamSettingsSnapshot = {
  userId: string;
  workspaceId: string;
  /** 팀·멤버 메타 시그니처 — 같으면 풀 로드 생략 */
  version: string;
  team: Team | null;
  membershipStatus: 'approved' | 'pending' | null;
  myRole: 'owner' | 'admin' | 'teacher' | 'viewer' | null;
  members: TeamMember[];
  pendingMembers: TeamMember[];
  savedAt: number;
};

function storageKey(userId: string, workspaceId: string): string {
  return `${STORAGE_KEY_PREFIX}.${SNAPSHOT_CACHE_VERSION}.${userId.trim()}.${workspaceId.trim()}`;
}

export function loadTeamSettingsSnapshot(
  userId: string,
  workspaceId: string,
): TeamSettingsSnapshot | null {
  const uid = userId.trim();
  const ws = workspaceId.trim();
  if (!uid || !ws || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(uid, ws));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TeamSettingsSnapshot;
    if (!parsed || parsed.userId !== uid || parsed.workspaceId !== ws) return null;
    if (typeof parsed.version !== 'string' || !parsed.version) return null;
    if (!Array.isArray(parsed.members) || !Array.isArray(parsed.pendingMembers)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveTeamSettingsSnapshot(snapshot: TeamSettingsSnapshot): void {
  const uid = snapshot.userId.trim();
  const ws = snapshot.workspaceId.trim();
  if (!uid || !ws || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      storageKey(uid, ws),
      JSON.stringify({ ...snapshot, savedAt: Date.now() }),
    );
  } catch {
    /* quota */
  }
}

export function clearTeamSettingsSnapshot(userId?: string, workspaceId?: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (userId?.trim() && workspaceId?.trim()) {
      window.localStorage.removeItem(storageKey(userId, workspaceId));
      return;
    }
    const prefix = `${STORAGE_KEY_PREFIX}.${SNAPSHOT_CACHE_VERSION}.`;
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(prefix)) continue;
      if (userId?.trim() && !key.includes(`.${userId.trim()}.`)) continue;
      keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/** 멤버십 행만으로 만든 짧은 버전 (프로필 이름 제외) */
export function buildMyMembershipVersion(
  row: {
    team_id?: string;
    role?: string;
    status?: string;
    is_active?: boolean;
    joined_at?: string | null;
    approved_at?: string | null;
    rejected_at?: string | null;
  } | null,
): string {
  if (!row?.team_id) return 'none';
  return [
    row.team_id,
    row.role ?? '',
    row.status ?? '',
    row.is_active ? '1' : '0',
    row.joined_at ?? '',
    row.approved_at ?? '',
    row.rejected_at ?? '',
  ].join('|');
}
